import { RARITY_IDS } from "@highmarch/domain";
import { WellOpts, wellHtml } from "../components";
import { RARITY_LABEL } from "../gearUi";

/**
 * The middle of every Shop item popup and Store pack popup (owner 2026-10-24: "tapping most items in the shop don't
 * open a pop up"): the item in its large well, its name in rarity ink with the rarity named under it (chests and
 * gear; nothing for goods that have none), and one short line of what it does. A pack lists what it holds, a well and
 * a name a row. Markup only (the tests read it); the dialog around it is itemPopup.ts.
 */

export interface ItemCard {
  /** The item's well (its art, count, cap, rarity tint) or ready markup (a painted icon). */
  readonly well: WellOpts | string;
  readonly name: string;
  /** A rarity index for chests and gear; null for goods without one (gold, speed-ups, supplies, treats, boosts). */
  readonly rarity: number | null;
  /** What it does, in a line ("+20% damage in every battle for the next 1h"). */
  readonly what: string;
  /** Extra markup under the card (the odds of a random roll, a struck base price). */
  readonly extra?: string;
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);

/** "Epic", "Legendary"… or "" for no rarity. */
export function rarityName(r: number | null): string {
  return r === null || r < 0 ? "" : RARITY_LABEL[r] ?? "";
}

/** One item: the large well, the name (rarity ink), the rarity, what it does. */
export function itemCardHtml(c: ItemCard): string {
  const well = typeof c.well === "string" ? `<span class="k-well lg sciconw">${c.well}</span>` : wellHtml({ ...c.well, size: "lg", tag: "span" });
  const r = rarityName(c.rarity), ink = r ? ` ri-${RARITY_IDS[c.rarity!]}` : "";
  return `<div class="scitem">${well}<div class="sct"><b class="disp${ink}">${esc(c.name)}</b>${r ? `<em class="scrar${ink}">${r}</em>` : ""}`
    + `${c.what ? `<small>${esc(c.what)}</small>` : ""}</div></div>${c.extra ?? ""}`;
}

/** One thing a pack holds: its well (ready markup), its name and what it is. */
export interface PackRow { readonly well: string; readonly name: string; readonly detail: string }

/** A Store pack: what it holds, a row each, then a line (the value, "Remaining: 1"). */
export function packCardHtml(rows: readonly PackRow[], lines: readonly string[] = []): string {
  const list = rows.map((r) => `<li class="sprow">${r.well}<span><b>${esc(r.name)}</b>${r.detail ? `<small>${esc(r.detail)}</small>` : ""}</span></li>`).join("");
  const foot = lines.filter(Boolean).map((l) => `<p class="spline">${l}</p>`).join("");
  return `${rows.length > 0 ? `<b class="ifhead">Inside</b><ul class="sprows">${list}</ul>` : ""}${foot}`;
}
